import { useState } from 'react'
import styled from '@emotion/styled'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'

import { ArrowIcon } from '@/assets/icons'
import { Flex } from '@/components/Flex'
import { CaretIcon } from '@/components/StyledIcon'
import { SubSidebarItem } from '@/components/SubSidebar/SubSidebarItem'
import { Label } from '@/components/Typography'
import { colors } from '@/constants'
import { useCurrentProvider, useUserProviders } from '../application/useProviders'
import { useSwitchProvider } from '../application/useSwitchProvider'

const ContentWrapper = styled.div`
  padding: 1.25rem;
  color: ${colors.light2};
  background-color: ${colors.darkBlue2};
  box-shadow: 0px 30px 40px 0px #0000004d;
  border: 0.5px solid ${colors.darkBlue4};
  border-radius: 1.25rem;
  z-index: 100;
`

const ProviderItem = styled(SubSidebarItem)`
  padding: 9px;
  height: 40px;
  border-radius: 10px;
`

export function ProviderSwitcher({
  containerId = 'header',
}: {
  containerId?: string
}) {
  const [open, setOpen] = useState(false)
  const providers = useUserProviders()
  const currentProvider = useCurrentProvider()
  const switchProvider = useSwitchProvider()

  if (!providers.data || !currentProvider.data) return null

  const current = currentProvider.data

  if (providers.data.length < 2) {
    return (
      <Flex alignItems="center">
        <Label color="light2">{current.name}</Label>
      </Flex>
    )
  }

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <Flex
          gap="10px"
          alignItems="center"
          onClick={() => setOpen(!open)}
          style={{ cursor: 'pointer' }}
        >
          <Label color="white">{current.name}</Label>
          <CaretIcon
            as={ArrowIcon}
            rotate={open}
            tabIndex={0}
            role="button"
            aria-label="Switch provider"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                setOpen(!open)
              }
            }}
          />
        </Flex>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal container={document.getElementById(containerId)}>
        <DropdownMenu.Content sideOffset={10} align="end">
          <ContentWrapper>
            <Flex width="225px" gap="20px" flexDirection="column">
              <Label color="white">Provider</Label>
              <Flex flexDirection="column" gap="0.25rem">
                {providers.data.map((provider) => (
                  <ProviderItem
                    key={provider.id}
                    isActive={provider.id === current.id}
                    onClick={() => {
                      setOpen(false)
                      if (
                        provider.id !== current.id &&
                        !switchProvider.isPending
                      ) {
                        switchProvider.mutate(provider.id)
                      }
                    }}
                  >
                    <Label
                      color={provider.id === current.id ? 'white' : 'light2'}
                    >
                      {provider.name}
                    </Label>
                  </ProviderItem>
                ))}
              </Flex>
            </Flex>
          </ContentWrapper>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}
